import { AxiosResponse } from 'axios';
import { jobBoardAPI } from './base';

const endpoints = {
  getNotifications: '/notifications',
  getAdminNotifications: '/notifications/admin',
  notificationCounts: '/notifications/unread-count',
  markAsRead: '/notifications/:id/read',
  markAsUnread: '/notifications/:id/unread',
  markAllAsRead: '/notifications/mark-all-read',
  markAsArchived: '/notifications/:id/archive',
  markAllAsArchived: '/notifications/archive-all',
  markAsUnArchived: '/notifications/:id/unarchive'
};

export interface Notification {
  read: boolean;
  id: string;
  userId: string;
  actorUserId?: string;
  actor?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    username?: string;
    profileImage?: string;
  };
  title: string;
  message: string;
  type: string;
  taskId?: string;
  jobId?: string;
  commentId?: string;
  metadata?: Record<string, any>;
  archived?: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface NotificationResponse {
  page: number;
  limit: number;
  total: number;
  data: Notification[];
}

export type AdminActivityEntityType = 'job' | 'task';

export interface AdminActivityItem {
  id: string;
  entityType: AdminActivityEntityType;
  activityType: string;
  description: string;
  metadata?: Record<string, any>;
  createdAt: string;
  user?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    username?: string;
    profileImage?: string;
  };
  job?: {
    id: string;
    title?: string;
    jobNumber?: string;
  };
  task?: {
    id: string;
    task?: string;
    description?: string;
    jobId?: any;
  };
}

export interface AdminActivityResponse {
  page: number;
  limit: number;
  total: number;
  data: AdminActivityItem[];
}

export interface NotificationCountResponse {
  count: number;
}

export interface GetNotificationsParams {
  page?: number;
  limit?: number;
  read?: boolean;
  type?: string;
  archived?: boolean;
  startDate?: string;
  endDate?: string;
  username?: string;
  jobNumber?: string;
}

export interface GetAdminActivityParams {
  page?: number;
  limit?: number;
  activityType?: AdminActivityEntityType;
  startDate?: string;
  endDate?: string;
  username?: string;
  jobNumber?: string;
}

export const getNotifications = async (
  params?: GetNotificationsParams
): Promise<AxiosResponse<NotificationResponse>> => {
  return jobBoardAPI.get(endpoints.getNotifications, { params });
};

export const getAdminNotifications = async (
  params?: GetAdminActivityParams
): Promise<AxiosResponse<AdminActivityResponse>> => {
  return jobBoardAPI.get(endpoints.getAdminNotifications, { params });
};

export const getNotificationCounts = async (): Promise<
  AxiosResponse<NotificationCountResponse>
> => {
  return jobBoardAPI.get(endpoints.notificationCounts);
};

export const markNotificationAsRead = async (
  notificationId: string
): Promise<AxiosResponse<Notification>> => {
  return jobBoardAPI.patch(endpoints.markAsRead.replace(':id', notificationId));
};

export const markAllNotificationsAsRead = async (): Promise<
  AxiosResponse<{ success: boolean }>
> => {
  return jobBoardAPI.patch(endpoints.markAllAsRead);
};

export const markNotificationAsArchived = async (
  notificationId: string
): Promise<AxiosResponse<Notification>> => {
  return jobBoardAPI.patch(
    endpoints.markAsArchived.replace(':id', notificationId)
  );
};

export const markAllNotificationsAsArchived = async (): Promise<
  AxiosResponse<{ success: boolean }>
> => {
  return jobBoardAPI.patch(endpoints.markAllAsArchived);
};

export const markNotificationAsUnArchived = async (
  notificationId: string
): Promise<AxiosResponse<Notification>> => {
  return jobBoardAPI.patch(
    endpoints.markAsUnArchived.replace(':id', notificationId)
  );
};

export const markNotificationAsUnread = async (
  notificationId: string
): Promise<AxiosResponse<Notification>> => {
  return jobBoardAPI.patch(
    endpoints.markAsUnread.replace(':id', notificationId)
  );
};
